import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { formatDate, readingLabel, type BlogPost } from '../data/blog';

const Card = styled.article`
  position: relative;
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 1.75rem;
  height: 100%;
  padding: clamp(1.25rem, 2.5vw, 2rem);
  border-radius: var(--radius-lg);
  background: var(--surface);
  border: 1px solid var(--border);
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:hover {
    border-color: var(--border-strong);
    background: var(--surface-hover);
  }

  &:focus-within {
    outline: 2px solid var(--focus);
    outline-offset: 3px;
  }

  a:focus-visible {
    outline: none;
  }
`;

const Cover = styled.img`
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 10;
  object-fit: cover;
  border-radius: var(--radius-md);
  border: 1px solid var(--border);
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
`;

export const PostMeta = styled.p`
  font-size: var(--fs-small);
  color: #A3A9B0;
`;

const Title = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  letter-spacing: -0.015em;

  a {
    color: inherit;
  }

  a::after {
    content: '';
    position: absolute;
    inset: 0;
  }
`;

const Excerpt = styled.p`
  color: #B8BDC2;
  font-size: 0.975rem;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
`;

export const PostTags = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;

  li {
    padding: 0.25rem 0.6rem;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 500;
    color: #B8BDC2;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid var(--border);
  }
`;

const COVER_SIZES = '(min-width: 961px) 320px, (min-width: 641px) calc(50vw - 5rem), calc(100vw - 5rem)';

interface PostCardProps {
  post: BlogPost;
  priority?: boolean;
}

const PostCard: React.FC<PostCardProps> = ({ post, priority = false }) => (
  <Card aria-labelledby={`post-card-${post.slug}`} lang={post.lang === 'es' ? 'es' : undefined}>
    {post.cover && (
      <Cover
        src={post.cover.src}
        srcSet={post.cover.srcSet}
        sizes={COVER_SIZES}
        alt={post.coverAlt ?? ''}
        width={post.cover.width}
        height={post.cover.height}
        loading={priority ? 'eager' : 'lazy'}
        fetchPriority={priority ? 'high' : undefined}
        decoding="async"
      />
    )}
    <Body>
      <PostMeta>
        <time dateTime={post.date}>{formatDate(post.date, post.lang)}</time> · {readingLabel(post.readingMinutes, post.lang)}
      </PostMeta>
      <Title id={`post-card-${post.slug}`}>
        <Link to={`/blog/${post.slug}`}>{post.title}</Link>
      </Title>
      <Excerpt>{post.description}</Excerpt>
      {post.tags.length > 0 && (
        <PostTags aria-label="Post tags">
          {post.tags.slice(0, 3).map((tag) => (
            <li key={tag}>{tag}</li>
          ))}
        </PostTags>
      )}
    </Body>
  </Card>
);

export default PostCard;
